import React, { useEffect, useLayoutEffect, useRef } from "react";

import { animated, type Interpolation, type SpringValue } from "react-spring";

import {
  PLAYHEAD_GLOW_COLORS,
  PLAYHEAD_PENTAGON_PATH,
  PLAYHEAD_SHADOW_BLUR,
  PLAYHEAD_SHADOW_OFFSET_X,
  PLAYHEAD_SHADOW_OPACITY,
  PLAYHEAD_PENTAGON_SHADOW_OPACITY,
} from "../constants";
import type { TimelinePlayerBridge } from "../bridge";
import { useTimelineDataStore, useTimelineStore } from "../store/timeline-context";
import { formatPointerTime } from "../utils/ruler";
import { placeTimeChip, TIME_CHIP_OFFSET_Y } from "./timeChip";

const PENTAGON_HALF_WIDTH = 5;
// The 1px line covers [x, x + 1], the same pixel column as the ruler ticks and
// the scrub bar's progress edge, so the pentagon centres on x + 0.5. An odd
// width puts that centre and its straight sides on whole pixels at any density.
const PENTAGON_WIDTH = PENTAGON_HALF_WIDTH * 2 + 1;
const PENTAGON_HEIGHT = 17;
const LINE_TOP_OFFSET = 16; // Line starts at the bottom of the pentagon.

type Props = {
  bridge: TimelinePlayerBridge;
  pixelsPerSecond: number;
  scrollLeft: number;
  isMinimized?: boolean;
  /** Height of the scrub-bar row that hosts the pentagon. */
  scrubBarHeight: number;
  /** Width of the canvas column — used to decide when the playhead has scrolled out of view. */
  viewportWidth: number;
  /** Number of rows to be rendered */
  numOfRenderableRows: number;
  /**
   * Animated height of the bottom timeline region (segments + summary).
   * Accepts both raw `SpringValue` and the `Interpolation` produced by `to([...], fn)`.
   */
  bottomHeight: SpringValue<number> | Interpolation<number>;
};

export interface PlayheadHandle {
  updatePosition: (time: number) => void;
  /** Brief cue that a scrub just snapped to an annotation boundary. */
  pulse: () => void;
}

const SNAP_PULSE_MS = 280;

/**
 * HTML/SVG playhead overlay — pentagon at top, vertical line below.
 *
 * Replaces the previous Konva `PlayheadLayer` (which had to be mounted twice —
 * once in the scrub-bar Stage, once in the main Stage — and kept aligned
 * shape-by-shape). Now there's a single absolutely-positioned div with the
 * pentagon and the line as its children.
 *
 * The comet-trail glow that used to live with the playhead now lives inside
 * `VideoTrackLayer` (the Konva scrub-bar Layer), where the Stage's bounds
 * clip it naturally — keeping it from bleeding into adjacent grid cells.
 *
 * Position is updated imperatively via `style.transform = "translateX(...)"`
 * on every time tick (subscribed to the bridge) and on every scroll/zoom
 * change. Hardware-accelerated; doesn't go through React's render path on
 * playback ticks.
 */
export const Playhead = React.forwardRef<PlayheadHandle, Props>(
  (
    {
      bridge,
      pixelsPerSecond,
      scrollLeft,
      isMinimized,
      scrubBarHeight,
      viewportWidth,
      bottomHeight,
      numOfRenderableRows,
    },
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const pentagonRef = useRef<SVGSVGElement>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);
    const lastXRef = useRef<number | null>(null);
    const lastVisibleRef = useRef<boolean | null>(null);
    const lastTimeRef = useRef(0);

    const isScrubbing = useTimelineStore((state) => state.isScrubbing);
    const durationSeconds = useTimelineDataStore((state) => state.durationSeconds);

    // Capture latest scroll/zoom/viewport in refs so the rAF callback always reads current values.
    const ppsRef = useRef(pixelsPerSecond);
    const scrollLeftRef = useRef(scrollLeft);
    const viewportWidthRef = useRef(viewportWidth);
    const scrubbingRef = useRef(isScrubbing);
    const durationRef = useRef(durationSeconds);
    ppsRef.current = pixelsPerSecond;
    scrollLeftRef.current = scrollLeft;
    viewportWidthRef.current = viewportWidth;
    scrubbingRef.current = isScrubbing;
    durationRef.current = durationSeconds;

    // While scrubbing, a bubble above the pentagon reads the time under it,
    // kept inside the canvas column at either end.
    const updateBubble = (time: number, x: number) => {
      const bubble = bubbleRef.current;
      if (!bubble || !scrubbingRef.current) return;
      placeTimeChip(bubble, formatPointerTime(time, ppsRef.current, durationRef.current), x, viewportWidthRef.current);
    };

    const apply = (time: number) => {
      lastTimeRef.current = time;
      // Round to integer pixels so the playhead's HTML/SVG line lands on the
      // same device-pixel column as the Konva content beneath it.
      const x = Math.round(time * ppsRef.current - scrollLeftRef.current);
      const w = viewportWidthRef.current;

      // Visible if the pentagon would have any overlap with the canvas column.
      // PENTAGON_HALF_WIDTH of natural overhang at the start (t=0) and end
      // (t=duration) is allowed so the marker is fully visible at the seekable
      // boundaries — anything beyond that means the playhead has scrolled out
      // of the visible scroll window and we hide the whole overlay so it
      // doesn't intrude on the time-display column on the left or appear in
      // dead space on the right.
      const visible = x >= -PENTAGON_HALF_WIDTH && x <= w + PENTAGON_HALF_WIDTH;

      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (x !== lastXRef.current) {
        lastXRef.current = x;
        wrapper.style.transform = `translateX(${x}px)`;
      }
      if (visible !== lastVisibleRef.current) {
        lastVisibleRef.current = visible;
        wrapper.style.visibility = visible ? "visible" : "hidden";
      }
      updateBubble(time, x);
    };

    const pulse = () => {
      const pentagon = pentagonRef.current;
      if (!pentagon || typeof pentagon.animate !== "function") return;
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      // A colour flash in the playhead's glow gold; the pop is motion, so it respects reduced motion.
      pentagon.animate(
        reduceMotion
          ? [{ color: PLAYHEAD_GLOW_COLORS[1] }, { color: "white" }]
          : [
              { color: PLAYHEAD_GLOW_COLORS[1], transform: "scale(1.35)" },
              { color: "white", transform: "scale(1)" },
            ],
        { duration: SNAP_PULSE_MS, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
      );
    };

    // `apply` and `pulse` read only refs, so the handle never needs replacing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useImperativeHandle(ref, () => ({ updatePosition: apply, pulse }), []);

    // Refresh on scroll/zoom/viewport changes (these flow through React) and
    // when a scrub starts or ends. A layout effect lands in the same frame as
    // the ruler, which updates at commit. While scrubbing, the scrub's own
    // position is authoritative: the video may still be finishing an earlier
    // seek, and edge auto-scroll re-renders every frame. This also fills the
    // bubble as soon as it mounts.
    useLayoutEffect(() => {
      apply(isScrubbing ? lastTimeRef.current : bridge.getCurrentTime());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bridge, pixelsPerSecond, scrollLeft, viewportWidth, isScrubbing]);

    // Imperative time-tick updates (don't trigger React renders). While
    // scrubbing, the scrub position wins over the video's still-settling seek.
    useEffect(() => {
      return bridge.subscribeToTime((time) => {
        if (!scrubbingRef.current) apply(time);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bridge]);

    // Line height = scrub-bar height + animated bottom-region height − the
    // top offset where the line begins (just below the pentagon).
    const lineHeight = bottomHeight.to((h: number) => Math.max(0, scrubBarHeight + h - LINE_TOP_OFFSET));

    const pentagonShadow = `drop-shadow(${PLAYHEAD_SHADOW_OFFSET_X}px 0 ${PLAYHEAD_SHADOW_BLUR}px rgba(0, 0, 0, ${PLAYHEAD_PENTAGON_SHADOW_OPACITY}))`;
    const lineShadow = `${PLAYHEAD_SHADOW_OFFSET_X}px 0 ${PLAYHEAD_SHADOW_BLUR}px rgba(0, 0, 0, ${PLAYHEAD_SHADOW_OPACITY})`;

    return (
      <div
        aria-hidden
        className="pointer-events-none absolute"
        ref={wrapperRef}
        style={{
          top: 0,
          left: 0,
          width: 1,
          zIndex: 10,
          willChange: "transform",
        }}
      >
        {/* Vertical line — rendered first so the pentagon sits on top of it.
            Hidden when minimized (matches the previous Konva behavior). */}
        {!isMinimized && numOfRenderableRows > 0 && (
          <animated.div
            style={{
              position: "absolute",
              top: LINE_TOP_OFFSET,
              left: 0,
              width: 1,
              height: lineHeight,
              backgroundColor: "white",
              boxShadow: lineShadow,
            }}
          />
        )}

        {/* Pentagon — last so it draws on top of the line and the comet-trail
            glow that the scrub-bar Stage paints beneath. */}
        <svg
          aria-hidden
          // The path is 10 units wide; stretch it across the odd 11px box.
          preserveAspectRatio="none"
          ref={pentagonRef}
          style={{
            position: "absolute",
            left: -PENTAGON_HALF_WIDTH,
            top: 0,
            width: PENTAGON_WIDTH,
            height: PENTAGON_HEIGHT,
            overflow: "visible",
            filter: pentagonShadow,
            color: "white",
            transformOrigin: "50% 0",
          }}
          viewBox={`-${PENTAGON_HALF_WIDTH} 0 ${PENTAGON_HALF_WIDTH * 2} ${PENTAGON_HEIGHT}`}
        >
          <path d={PLAYHEAD_PENTAGON_PATH} fill="currentColor" />
        </svg>

        {isScrubbing && (
          <div
            className="absolute whitespace-nowrap rounded bg-white px-1.5 py-0.5 text-[10px] font-medium leading-none text-black shadow-sm"
            ref={bubbleRef}
            style={{ top: -TIME_CHIP_OFFSET_Y, left: 0, fontVariantNumeric: "tabular-nums" }}
          />
        )}
      </div>
    );
  },
);

Playhead.displayName = "Playhead";
